import type {
  AdminStatsResponse,
  AdminUser,
  GameHistoryResponse,
  LeaderboardEntry,
  ProfileStatsResponse,
} from '@sea-wars/protocol';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Mode, Skin, ThemeOption } from '../App.js';
import { ApiError, api } from '../api/client.js';
import { useSession } from '../api/session.js';
import { ShareLink } from '../components/ShareLink.js';
import { useInstallPrompt } from '../useInstallPrompt.js';

const percent = (value: number | null): string =>
  value === null ? '—' : `${Math.round(value * 100)}%`;

interface AccountPageProps {
  readonly skin: Skin;
  readonly setSkin: (skin: Skin) => void;
  readonly mode: Mode;
  readonly setMode: (mode: Mode) => void;
  readonly themeOptions: readonly ThemeOption[];
}

/**
 * "Moje konto" — statystyki, ranking i historia własnych partii, motyw, zmiana hasła,
 * a dla administratorów także panel obsługi użytkowników (dawniej osobna trasa `/admin` —
 * przeniesiony tutaj, żeby wszystko dotyczące konta i jego uprawnień żyło w jednym
 * miejscu). Docelowo też kolejne funkcje związane z własnym kontem (np. zmiana
 * e-maila/nicku, eksport/usunięcie danych — `docs/06-obserwowalnosc-i-rodo.md` §6.5).
 *
 * `skin`/`mode` przychodzą propsami z `App.tsx` — stan musi działać na każdej trasie
 * (motyw dotyczy całego dokumentu), więc mieszka wyżej; ta strona tylko go pokazuje/zmienia.
 */
export function AccountPage({ skin, setSkin, mode, setMode, themeOptions }: AccountPageProps) {
  const { user, signOut } = useSession();
  const [stats, setStats] = useState<ProfileStatsResponse | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[] | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);

  useEffect(() => {
    api
      .stats()
      .then(setStats)
      .catch(() => setStats(null));
    api
      .leaderboard()
      .then((response) => setLeaderboard(response.items))
      .catch(() => setLeaderboard([]));
    api
      .inviteCode()
      .then((response) => setInviteCode(response.code))
      .catch(() => setInviteCode(null));
  }, []);

  return (
    <>
      <div className="card">
        <h2 className="hand">Moje konto</h2>
        <p className="app__note">
          Zalogowano jako <strong>{user?.nickname}</strong>
          {user?.isAdmin && ' (admin)'}.
        </p>
      </div>

      <div className="card-row">
        <InstallCard />
        <div className="card card--narrow">
          <h2 className="hand">Motyw</h2>
          <label className="field">
            <span>Zeszyt/tablica, jasny/ciemny</span>
            <select
              value={`${skin}-${mode}`}
              onChange={(event) => {
                const option = themeOptions.find((o) => o.value === event.target.value);
                if (!option) return;
                setSkin(option.skin);
                setMode(option.mode);
              }}
            >
              {themeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {inviteCode && user && (
        <div className="card">
          <h2 className="hand">Zaproś znajomego</h2>
          <p className="app__note">
            Ten link od razu wypełnia kod zaproszenia — znajomy tylko wybiera nick i hasło.
          </p>
          <InviteSignupLink code={inviteCode} referrerId={user.id} />
        </div>
      )}

      {stats && stats.gamesPlayed > 0 && (
        <div className="card">
          <h2 className="hand">Statystyki</h2>
          <div className="stats">
            <div className="stats__tile">
              <span className="stats__value">{stats.gamesPlayed}</span>
              <span className="stats__label">rozegrane</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{stats.wins}</span>
              <span className="stats__label">wygrane</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{percent(stats.winRate)}</span>
              <span className="stats__label">% zwycięstw</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{percent(stats.accuracy)}</span>
              <span className="stats__label">celność</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{stats.longestHitStreak}</span>
              <span className="stats__label">najdłuższa seria trafień</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">
                {stats.currentStreak
                  ? `${stats.currentStreak.type === 'win' ? '+' : '−'}${stats.currentStreak.count}`
                  : '—'}
              </span>
              <span className="stats__label">aktualna passa</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{Math.round(stats.rating)}</span>
              <span className="stats__label">ranking (RD {Math.round(stats.ratingDeviation)})</span>
            </div>
          </div>
          {stats.bestGame && (
            <p className="app__note">
              Najlepsza partia:{' '}
              <Link to={`/replay/${stats.bestGame.gameId}`}>
                {stats.bestGame.shots} {stats.bestGame.shots === 1 ? 'strzał' : 'strzałów'} przeciw{' '}
                {stats.bestGame.opponent?.nickname ?? 'nieznanemu graczowi'}
              </Link>
            </p>
          )}
        </div>
      )}

      {leaderboard && leaderboard.length > 0 && (
        <div className="card">
          <h2 className="hand">Ranking</h2>
          <div className="table-scroll">
            <table className="history">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Gracz</th>
                  <th scope="col">Rating</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((row, index) => (
                  <tr
                    key={row.nickname}
                    className={row.nickname === user?.nickname ? 'history__win' : undefined}
                  >
                    <td>{index + 1}</td>
                    <td>{row.nickname}</td>
                    <td>{Math.round(row.rating)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <HistorySection />

      {user?.isAdmin && <AdminSection selfId={user.id} />}

      <div className="card-row">
        <ChangePasswordCard />
        <div className="card card--narrow">
          <h2 className="hand">Wyloguj</h2>
          <p className="app__note">Kończy bieżącą sesję na tym urządzeniu.</p>
          <button type="button" className="btn btn--ghost" onClick={() => void signOut()}>
            Wyloguj
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * `ref` w linku (informacyjne, patrz `auth/accounts.ts`) i `kod` z góry wypełniają formularz
 * rejestracji. Prop nazwany `referrerId`, nie `ref` — to drugie jest zarezerwowane przez Reacta.
 */
function InviteSignupLink({ code, referrerId }: { code: string; referrerId: string }) {
  const url = `${window.location.origin}/rejestracja?kod=${encodeURIComponent(code)}&ref=${referrerId}`;
  return (
    <ShareLink
      url={url}
      message="Zagraj ze mną w Statki!"
      inputLabel="Link zaproszenia do rejestracji"
    />
  );
}

/**
 * Rozegrane partie — od razu tylko ostatnie `HISTORY_PAGE_SIZE`, żeby nie zalewać "Moje konto"
 * długą tabelą; "Pokaż więcej" odsłania stronicowanie (kursor z API to znacznik czasu ostatniej
 * pozycji poprzedniej strony, patrz `routes/games.ts`). Odwiedzone strony trzymamy w tablicy,
 * żeby "Poprzednia" nie musiała niczego pobierać ponownie.
 */
const HISTORY_PAGE_SIZE = 10;

function HistorySection() {
  const [pages, setPages] = useState<GameHistoryResponse[] | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    api
      .history({ limit: HISTORY_PAGE_SIZE })
      .then((response) => setPages([response]))
      .catch(() => setPages([{ items: [], nextCursor: null }]));
  }, []);

  const currentPage = pages?.[pageIndex] ?? null;

  async function loadNext() {
    if (!pages) return;
    // Strona już wcześniej pobrana (np. po "Poprzednia") — przesuwamy się bez ponownego żądania.
    if (pageIndex + 1 < pages.length) {
      setPageIndex((index) => index + 1);
      return;
    }
    const cursor = pages.at(-1)?.nextCursor;
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const next = await api.history({ cursor, limit: HISTORY_PAGE_SIZE });
      setPages((prev) => [...(prev ?? []), next]);
      setPageIndex((index) => index + 1);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="card">
      <h2 className="hand">Rozegrane partie</h2>
      {pages === null && <p>Wczytywanie…</p>}
      {currentPage && currentPage.items.length === 0 && pageIndex === 0 && (
        <p className="app__note">Jeszcze nic tu nie ma. Zagraj pierwszą partię.</p>
      )}
      {currentPage && currentPage.items.length > 0 && (
        <>
          <div className="table-scroll">
            <table className="history">
              <thead>
                <tr>
                  <th scope="col">Przeciwnik</th>
                  <th scope="col">Wynik</th>
                  <th scope="col">Ruchy</th>
                  <th scope="col">Celność</th>
                  <th scope="col">Kiedy</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {currentPage.items.map((game) => (
                  <tr key={game.id}>
                    <td>{game.opponent?.nickname ?? '—'}</td>
                    <td className={game.result === 'win' ? 'history__win' : 'history__loss'}>
                      {game.result === 'win'
                        ? 'wygrana'
                        : game.result === 'loss'
                          ? 'przegrana'
                          : '—'}
                    </td>
                    <td>{game.moves}</td>
                    <td>{game.accuracy === null ? '—' : `${Math.round(game.accuracy * 100)}%`}</td>
                    <td>
                      {game.finishedAt
                        ? new Date(game.finishedAt).toLocaleString('pl-PL', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })
                        : '—'}
                    </td>
                    <td>
                      <Link to={`/replay/${game.id}`} className="btn btn--ghost btn--small">
                        Replay
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!expanded && currentPage.nextCursor && (
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => setExpanded(true)}
            >
              Pokaż więcej partii
            </button>
          )}
          {expanded && (
            <div className="history__pager">
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={pageIndex === 0}
                onClick={() => setPageIndex((index) => Math.max(0, index - 1))}
              >
                ← Poprzednia
              </button>
              <span className="app__note">Strona {pageIndex + 1}</span>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={!currentPage.nextCursor || loadingMore}
                onClick={() => void loadNext()}
              >
                {loadingMore ? 'Wczytywanie…' : 'Następna →'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Instalacja PWA — ukryta zupełnie, gdy nie ma czego pokazać (już zainstalowana, albo
 * przeglądarka nie wspiera ani `beforeinstallprompt`, ani nie jest iOS Safari z jego
 * osobną, ręczną drogą) — patrz `useInstallPrompt`.
 */
function InstallCard() {
  const { installed, canInstall, promptInstall, isIos } = useInstallPrompt();
  if (installed || (!canInstall && !isIos)) return null;

  return (
    <div className="card card--narrow">
      <h2 className="hand">Zainstaluj na telefonie</h2>
      <p className="app__note">
        Statki jako aplikacja na ekranie głównym — bez paska adresu, działa też offline.
      </p>
      {canInstall && (
        <button type="button" className="btn btn--primary" onClick={() => void promptInstall()}>
          Zainstaluj aplikację
        </button>
      )}
      {!canInstall && isIos && (
        <p className="app__note">
          W Safari dotknij ikonę udostępniania (kwadrat ze strzałką na dole ekranu), potem „Dodaj do
          ekranu początkowego".
        </p>
      )}
    </div>
  );
}

/**
 * Formularz żyje w `<dialog>` zamiast na stałe w karcie — rzadko używana akcja nie musi
 * zajmować miejsca obok "Wyloguj" na stałe. Ten sam wzorzec co `.bot-dialog`/`.notice-dialog`
 * (`LobbyPage.tsx`, `NotificationPopup.tsx`): `showModal()`/`close()` sterują widocznością,
 * `[open]` w CSS pilnuje, żeby zamknięty dialog naprawdę zniknął.
 */
function ChangePasswordCard() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);

  function openDialog() {
    setError(null);
    setSuccess(false);
    dialogRef.current?.showModal();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setError('Nowe hasła nie są takie same');
      return;
    }

    setBusy(true);
    try {
      await api.changePassword({ currentPassword, newPassword });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Nie udało się zmienić hasła');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card card--narrow">
        <h2 className="hand">Zmień hasło</h2>
        <p className="app__note">Aktualne hasło i nowe — dwa razy, dla pewności.</p>
        <button type="button" className="btn" onClick={openDialog}>
          Zmień hasło
        </button>
      </div>

      <dialog ref={dialogRef} className="form-dialog">
        <form onSubmit={submit}>
          <h2 className="hand">Zmień hasło</h2>
          <label className="field">
            <span>Aktualne hasło</span>
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label className="field">
            <span>Nowe hasło</span>
            <input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>
          <label className="field">
            <span>Powtórz nowe hasło</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>
          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}
          {success && (
            <p className="app__note" role="status">
              Hasło zmienione. Inne otwarte sesje tego konta zostały wylogowane.
            </p>
          )}
          <div className="notice-dialog__actions">
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Zmienianie…' : 'Zmień hasło'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => dialogRef.current?.close()}
            >
              {success ? 'Zamknij' : 'Anuluj'}
            </button>
          </div>
        </form>
      </dialog>
    </>
  );
}

/**
 * Panel admina, dawniej osobna trasa `/admin` — przeniesiony do "Moje konto" na życzenie
 * użytkownika, żeby wszystko związane z kontem/uprawnieniami żyło w jednym miejscu.
 * Dostęp nadal wyłącznie serwerowy (`requireAdmin` w API, 404 dla nie-admina) — to tylko
 * przeniesienie UI, żadna zmiana w modelu uprawnień.
 */
function AdminSection({ selfId }: { selfId: string }) {
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState<{ nickname: string; password: string } | null>(
    null,
  );

  useEffect(() => {
    api.adminStats().then(setStats);
    api
      .adminUsers()
      .then((response) => setUsers(response.items))
      .catch(() => setUsers([]));
  }, []);

  function refreshUsers() {
    api
      .adminUsers()
      .then((response) => setUsers(response.items))
      .catch(() => setUsers([]));
  }

  async function toggleStatus(target: AdminUser) {
    setBusyId(target.id);
    try {
      await api.setUserStatus(target.id, target.status === 'active' ? 'blocked' : 'active');
      refreshUsers();
    } finally {
      setBusyId(null);
    }
  }

  async function toggleAdmin(target: AdminUser) {
    setBusyId(target.id);
    try {
      await api.setUserAdmin(target.id, !target.isAdmin);
      refreshUsers();
    } finally {
      setBusyId(null);
    }
  }

  async function resetPassword(target: AdminUser) {
    if (
      !window.confirm(
        `Zresetować hasło „${target.nickname}"? Dotychczasowe sesje tego konta zostaną wylogowane.`,
      )
    ) {
      return;
    }
    setBusyId(target.id);
    try {
      const result = await api.resetUserPassword(target.id);
      setNewPassword({ nickname: target.nickname, password: result.password });
    } finally {
      setBusyId(null);
    }
  }

  async function deleteUser(target: AdminUser) {
    if (
      !window.confirm(
        `Usunąć i zanonimizować konto „${target.nickname}"? Tego nie da się odwrócić.`,
      )
    ) {
      return;
    }
    setBusyId(target.id);
    try {
      await api.deleteUser(target.id);
      refreshUsers();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <h2 className="hand admin-section__title">Panel admina</h2>

      {newPassword && (
        <div className="card">
          <h2 className="hand">Nowe hasło dla „{newPassword.nickname}"</h2>
          <p className="app__note">
            Zapisz je i przekaż użytkownikowi teraz — nie da się go zobaczyć ponownie. Dotychczasowe
            sesje tego konta zostały wylogowane.
          </p>
          <p className="invite__link">
            <input
              readOnly
              value={newPassword.password}
              onFocus={(event) => event.target.select()}
              aria-label="Nowe hasło jednorazowe"
            />
            <button
              type="button"
              className="btn"
              onClick={() =>
                void navigator.clipboard?.writeText(newPassword.password).catch(() => {})
              }
            >
              Kopiuj
            </button>
          </p>
          <button type="button" className="btn btn--ghost" onClick={() => setNewPassword(null)}>
            Zamknij
          </button>
        </div>
      )}

      {stats === null && <p className="app__status">Wczytywanie…</p>}
      {stats && (
        <div className="card">
          <div className="stats">
            <div className="stats__tile">
              <span className="stats__value">{stats.totalAccounts}</span>
              <span className="stats__label">konta</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{stats.totalGames}</span>
              <span className="stats__label">partie łącznie</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{stats.gamesInProgress}</span>
              <span className="stats__label">partie w toku</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{stats.gamesFinishedLast7Days}</span>
              <span className="stats__label">zakończone (7 dni)</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{stats.totalMoves}</span>
              <span className="stats__label">strzały łącznie</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">{stats.activeUsersLast7Days}</span>
              <span className="stats__label">aktywni (7 dni)</span>
            </div>
            <div className="stats__tile">
              <span className="stats__value">
                {stats.placementAbandonmentRate === null
                  ? '—'
                  : `${Math.round(stats.placementAbandonmentRate * 100)}%`}
              </span>
              <span className="stats__label">porzucenia w rozstawianiu</span>
            </div>
          </div>
          <p className="app__note">
            Reszta metryk z <code>docs/06-obserwowalnosc-i-rodo.md</code> §6.3 (mediana czasu
            partii, % partii z rozłączeniem…) doczepi się tu w miarę napływu danych.
          </p>
        </div>
      )}

      <div className="card">
        <h2 className="hand">Użytkownicy</h2>
        {users === null && <p>Wczytywanie…</p>}
        {users?.length === 0 && <p className="app__note">Jeszcze nikt się nie zarejestrował.</p>}
        {users && users.length > 0 && (
          <div className="table-scroll">
            <table className="history">
              <thead>
                <tr>
                  <th scope="col">Nick</th>
                  <th scope="col">E-mail</th>
                  <th scope="col">Rola</th>
                  <th scope="col">Status</th>
                  <th scope="col">Zaproszony przez</th>
                  <th scope="col">Ostatnio widziany</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {users.map((row) => {
                  const isSelf = row.id === selfId;
                  const busy = busyId === row.id;
                  return (
                    <tr key={row.id}>
                      <td>{row.nickname}</td>
                      <td>{row.email ?? '—'}</td>
                      <td>{row.isAdmin ? 'admin' : 'gracz'}</td>
                      <td className={row.status === 'blocked' ? 'history__loss' : undefined}>
                        {row.status === 'blocked' ? 'zablokowany' : 'aktywny'}
                      </td>
                      <td>{row.invitedByNickname ?? '—'}</td>
                      <td>
                        {row.lastSeenAt
                          ? new Date(row.lastSeenAt).toLocaleString('pl-PL', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })
                          : '—'}
                      </td>
                      <td className="admin-users__actions">
                        {!isSelf && (
                          <>
                            <button
                              type="button"
                              className="btn btn--ghost btn--small"
                              disabled={busy}
                              onClick={() => void toggleStatus(row)}
                            >
                              {row.status === 'blocked' ? 'Odblokuj' : 'Zablokuj'}
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost btn--small"
                              disabled={busy}
                              onClick={() => void toggleAdmin(row)}
                            >
                              {row.isAdmin ? 'Odbierz admina' : 'Nadaj admina'}
                            </button>
                            {row.kind === 'user' && (
                              <button
                                type="button"
                                className="btn btn--ghost btn--small"
                                disabled={busy}
                                onClick={() => void resetPassword(row)}
                              >
                                Resetuj hasło
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn--ghost btn--small"
                              disabled={busy}
                              onClick={() => void deleteUser(row)}
                            >
                              Usuń
                            </button>
                          </>
                        )}
                        {isSelf && <span className="app__note">to Ty</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
